/**
 * Round-up pipeline (BRIEF §9, §15, §17, §18, §31).
 *
 * Responsibilities:
 *  - turn detected card spends into RoundupInvestment records, exactly once
 *  - queue round-ups until the batch clears the viable bridge minimum and the
 *    reserve can cover it (reserve debit never exceeds the summed round-ups)
 *  - execute queued batches: reserve → [gas top-up] → bridge → USDG → stock
 *  - distribute batch results proportionally across the records
 *  - resume batches interrupted at any stage, without ever double-spending
 *
 * Execution pools, processed in order:
 *  C. bridge_pending WITH a bridgeTxHash — a bridge submitted earlier;
 *     resolve its delivery (waitForCompletion), never re-submit.
 *  D. swap_pending WITH a stockSwapTxHash — a swap submitted earlier;
 *     resolve its receipt, never re-submit.
 *  B. bridge_complete — USDG stranded on the destination; run swap only.
 *  A. queued — fresh batches; full pipeline.
 *
 * Destination gas (owner decision 2026-08-10): when the wallet can't pay for
 * the swap on Robinhood Chain, a slice of the batch is converted to ETH
 * first. The slice comes OUT of the round-up value — the reserve debit never
 * exceeds the summed round-ups, and the gas cost shows up as execution cost.
 *
 * The engine reads its deps through a getter so demo/live adapters and
 * reserve providers can switch with settings without rebuilding mid-flight.
 */
import {
  allRoundups,
  getDemoReservePence,
  getSettings,
  insertRoundupIfNew,
  putRoundup,
  putSpend,
  setDemoReservePence,
} from "../db";
import { fiatMinorToUsdCents, usdCentsToFiatMinor, usdCentsToTokenUnits, tokenUnitsToUsdCents } from "../money";
import { calcRoundupPence, type RoundingIncrement } from "../roundup";
import type { DetectedSpend, FiatCurrency, Hex, RoundupInvestment } from "../types";
import type { BridgeAdapter } from "../bridge/types";
import type { SwapAdapter } from "../swap/types";

export type EngineEvent =
  | { type: "spend_detected"; spend: DetectedSpend }
  | { type: "roundup_queued"; roundup: RoundupInvestment }
  | { type: "roundup_skipped"; roundup: RoundupInvestment }
  | { type: "executing"; count: number; totalPence: number }
  | {
      type: "invested";
      stockSymbol: string;
      investedPence: number;
      roundupPence: number;
      costPence: number;
      count: number;
      currency: FiatCurrency;
    }
  | { type: "error"; message: string };

export interface EngineDeps {
  bridge: BridgeAdapter;
  swap: SwapAdapter;
  /** micro-USD per unit of the requested card currency. */
  getUsdPerCurrencyE6: (currency: FiatCurrency) => Promise<number>;
  /**
   * Static floor (pence) below which a queued batch keeps accumulating
   * (BRIEF §18). Mode-dependent: pennies in demo, ~£5 for the live Gnosis
   * route where fixed bridge fees dominate small transfers.
   */
  getMinViablePence: (currency: FiatCurrency) => number;
  getReservePence: (currency: FiatCurrency) => Promise<number>;
  /** Demo: mutate the simulated balance. Live: no-ops (real txs move funds). */
  debitReservePence: (currency: FiatCurrency, pence: number) => Promise<void>;
  creditReservePence: (currency: FiatCurrency, pence: number) => Promise<void>;
  /**
   * Live only: ensure the wallet can pay swap gas on the destination chain,
   * converting up to a capped slice of the batch. Returns units consumed.
   */
  prepareDestinationGas?: (batchUnits: bigint) => Promise<{ gasSpentUnits: bigint }>;
  onEvent: (e: EngineEvent) => void;
}

export function makeDemoReserve() {
  return {
    getReservePence: (_currency: FiatCurrency) => getDemoReservePence(),
    debitReservePence: async (_currency: FiatCurrency, pence: number) => {
      const cur = await getDemoReservePence();
      await setDemoReservePence(Math.max(0, cur - pence));
    },
    creditReservePence: async (_currency: FiatCurrency, pence: number) => {
      const cur = await getDemoReservePence();
      await setDemoReservePence(cur + pence);
    },
  };
}

export class RoundupEngine {
  private executing = false;

  constructor(private getDeps: () => EngineDeps) {}

  /**
   * Ingest one detected spend. Idempotent: the (txHash, logIndex) key is
   * inserted at most once, so re-scanning old blocks is harmless.
   */
  async processSpend(spend: DetectedSpend): Promise<void> {
    const deps = this.getDeps();
    await putSpend(spend);
    deps.onEvent({ type: "spend_detected", spend });

    if (spend.kind === "refund") return; // BRIEF §32: investment stands.

    const settings = await getSettings();
    const roundupPence = calcRoundupPence(
      spend.amountPence,
      settings.incrementPence as RoundingIncrement,
    );

    const record: RoundupInvestment = {
      id: `${spend.txHash}:${spend.logIndex}`,
      cardTxHash: spend.txHash,
      logIndex: spend.logIndex,
      blockNumber: spend.blockNumber,
      timestamp: spend.timestamp,
      merchant: spend.merchant,
      purchasePence: spend.amountPence,
      currency: spend.currency,
      incrementPence: settings.incrementPence,
      roundupPence,
      status: roundupPence === 0 ? "skipped" : "queued",
      statusDetail: roundupPence === 0 ? "Exact amount — nothing to round up" : undefined,
      attempts: 0,
      stockSymbol: settings.stockSymbol,
    };

    const inserted = await insertRoundupIfNew(record);
    if (!inserted) return; // already processed — never double-invest

    deps.onEvent(
      record.status === "skipped"
        ? { type: "roundup_skipped", roundup: record }
        : { type: "roundup_queued", roundup: record },
    );
  }

  /**
   * Reset a failed record to the correct earlier stage so the next
   * executeEligible() resumes it — WITHOUT repeating stages whose funds
   * already moved.
   */
  async retry(id: string): Promise<void> {
    const records = await allRoundups();
    const r = records.find((x) => x.id === id);
    if (!r || r.status !== "failed") return;
    let status: RoundupInvestment["status"];
    if (r.destinationAmountUnits) status = "bridge_complete"; // USDG landed → swap only
    else if (r.bridgeTxHash) status = "bridge_pending"; // bridge in flight → resolve it
    else status = "queued"; // nothing moved → full pipeline
    await putRoundup({ ...r, status, statusDetail: undefined });
  }

  /**
   * Startup recovery (BRIEF §31): records stuck mid-flight from a previous
   * session are routed to the right resume pool. Only records with NO
   * on-chain footprint are failed-retryable; anything with a tx hash keeps
   * its state so the resume pools can resolve it.
   */
  async recoverUnfinished(): Promise<void> {
    const records = await allRoundups();
    for (const r of records) {
      const midFlight =
        r.status === "bridge_pending" || r.status === "swap_pending" || r.status === "bridge_complete";
      if (!midFlight) continue;
      const hasFootprint = !!r.bridgeTxHash || !!r.stockSwapTxHash || !!r.destinationAmountUnits;
      if (!hasFootprint) {
        await putRoundup({
          ...r,
          status: "failed",
          statusDetail: "Interrupted before submission — will retry",
        });
      }
      // else: leave in place; executeEligible() resumes it.
    }
  }

  /** Run all resume pools + eligible fresh batches. Serialised. */
  async executeEligible(): Promise<void> {
    if (this.executing) return;
    this.executing = true;
    try {
      await this.resolveInFlightBridges();
      await this.resolveInFlightSwaps();
      await this.swapStrandedBatches();
      await this.executeFreshBatches();
    } catch (err) {
      this.getDeps().onEvent({
        type: "error",
        message: String(err instanceof Error ? err.message : err),
      });
    } finally {
      this.executing = false;
    }
  }

  // ---------- pool C: bridges submitted but not resolved ----------

  private async resolveInFlightBridges(): Promise<void> {
    const deps = this.getDeps();
    const records = await allRoundups();
    const groups = groupBy(
      records.filter((r) => r.status === "bridge_pending" && r.bridgeTxHash),
      (r) => r.bridgeTxHash!,
    );
    for (const [txHash, group] of groups) {
      try {
        const res = await deps.bridge.waitForCompletion(txHash as Hex);
        if (res.outputUnits === 0n) throw new Error("Bridge output unknown");
        for (const r of group) {
          await putRoundup({
            ...r,
            status: "bridge_complete",
            destinationAmountUnits: res.outputUnits.toString(),
            statusDetail: undefined,
          });
        }
      } catch (err) {
        for (const r of group) {
          await putRoundup({
            ...r,
            status: "failed",
            statusDetail: `Bridge unresolved: ${errMsg(err)}`,
          });
        }
      }
    }
  }

  // ---------- pool D: swaps submitted but not resolved ----------

  private async resolveInFlightSwaps(): Promise<void> {
    const deps = this.getDeps();
    const records = await allRoundups();
    const groups = groupBy(
      records.filter((r) => r.status === "swap_pending" && r.stockSwapTxHash),
      (r) => r.stockSwapTxHash!,
    );
    for (const [txHash, group] of groups) {
      try {
        const res = await deps.swap.resolve(txHash as Hex, group[0].stockSymbol);
        // Post-crash we no longer know the swap quote's fee split; value the
        // batch at its bridged USDG amount (capped at the round-up total).
        const investedUsdCents = tokenUnitsToUsdCents(
          BigInt(group[0].destinationAmountUnits ?? "0"),
          6,
        );
        await this.complete(group, res.stockUnitsReceived, res.txHash, investedUsdCents);
      } catch (err) {
        for (const r of group) {
          await putRoundup({
            ...r,
            status: "failed",
            statusDetail: `Swap unresolved: ${errMsg(err)}`,
          });
        }
      }
    }
  }

  // ---------- pool B: USDG stranded on destination ----------

  private async swapStrandedBatches(): Promise<void> {
    const deps = this.getDeps();
    const records = await allRoundups();
    const groups = groupBy(
      records.filter((r) => r.status === "bridge_complete" && r.destinationAmountUnits),
      (r) => r.bridgeTxHash ?? r.id,
    );
    for (const [, group] of groups) {
      const inputUnits = BigInt(group[0].destinationAmountUnits!);
      try {
        await this.runSwapLeg(group, inputUnits);
      } catch (err) {
        for (const r of group) {
          await putRoundup({ ...r, status: "failed", statusDetail: `Swap failed: ${errMsg(err)}` });
        }
        deps.onEvent({ type: "error", message: errMsg(err) });
      }
    }
  }

  // ---------- pool A: fresh queued batches ----------

  private async executeFreshBatches(): Promise<void> {
    const records = await allRoundups();
    const queued = records.filter((r) => r.status === "queued").sort((a, b) => a.timestamp - b.timestamp);
    if (queued.length === 0) return;

    // Never mix targets or fiat denominations. The bridge/swap and FX result
    // is shared across a batch, so every member must have identical routing.
    const groups = groupBy(queued, (r) => `${r.stockSymbol}:${r.currency}`);
    for (const [, group] of groups) await this.executeFreshBatch(group);
  }

  private async executeFreshBatch(queued: RoundupInvestment[]): Promise<void> {
    const deps = this.getDeps();
    assertHomogeneousBatch(queued);
    const currency = queued[0].currency;

    const totalPence = queued.reduce((s, r) => s + r.roundupPence, 0);

    // Batch below the economically viable minimum → keep accumulating (§18).
    if (totalPence < deps.getMinViablePence(currency)) {
      await annotate(queued, "Waiting for minimum investment size");
      return;
    }

    // Reserve must cover the whole batch; card spending is never affected (§15).
    const reserve = await deps.getReservePence(currency);
    if (reserve < totalPence) {
      await annotate(queued, "Waiting for reserve top-up");
      return;
    }

    const usdPerCurrencyE6 = await deps.getUsdPerCurrencyE6(currency);
    const totalUsdCents = fiatMinorToUsdCents(totalPence, usdPerCurrencyE6);
    const batchUnits = usdCentsToTokenUnits(totalUsdCents, 6);

    // Destination gas comes out of the batch (never on top of the round-up).
    let gasSpentUnits = 0n;
    if (deps.prepareDestinationGas) {
      try {
        const prep = await deps.prepareDestinationGas(batchUnits);
        gasSpentUnits = prep.gasSpentUnits;
      } catch (err) {
        await annotate(queued, errMsg(err));
        return; // nothing bridged yet — batch simply stays queued
      }
    }
    const bridgeUnits = batchUnits - gasSpentUnits;

    let bridgeQuote;
    try {
      bridgeQuote = await deps.bridge.getQuote(bridgeUnits);
    } catch (err) {
      await annotate(queued, `No bridge route right now: ${errMsg(err)}`);
      return;
    }
    if (bridgeUnits < bridgeQuote.minInputUnits) {
      await annotate(queued, "Waiting for minimum investment size");
      return;
    }

    deps.onEvent({ type: "executing", count: queued.length, totalPence });

    const mark = async (status: RoundupInvestment["status"], patch: Partial<RoundupInvestment> = {}) => {
      for (let i = 0; i < queued.length; i++) {
        queued[i] = { ...queued[i], status, statusDetail: undefined, ...patch };
        await putRoundup(queued[i]);
      }
    };

    // ---- reserve debit: exactly the summed round-ups, never more (§17) ----
    await deps.debitReservePence(currency, totalPence);

    let bridgeSubmitted = false;
    try {
      await mark("bridge_pending", { attempts: queued[0].attempts + 1 });
      const bridgeTxHash = await deps.bridge.submit(bridgeQuote);
      bridgeSubmitted = true;
      await mark("bridge_pending", { bridgeTxHash }); // persist BEFORE waiting
      const bridgeRes = await deps.bridge.waitForCompletion(bridgeTxHash);
      await mark("bridge_complete", {
        destinationAmountUnits: bridgeRes.outputUnits.toString(),
      });

      await this.runSwapLeg(queued, bridgeRes.outputUnits);
    } catch (err) {
      // Funds only moved if the bridge tx went out; before that, refund the
      // (demo) reserve. Live-mode debit/credit are no-ops — real funds are
      // still in the wallet because nothing was submitted.
      if (!bridgeSubmitted) await deps.creditReservePence(currency, totalPence);
      await mark("failed", { statusDetail: `Execution failed: ${errMsg(err)}` });
      throw err;
    }
  }

  // ---------- shared swap leg + attribution ----------

  private async runSwapLeg(group: RoundupInvestment[], inputUnits: bigint): Promise<void> {
    const deps = this.getDeps();
    assertHomogeneousBatch(group);
    const symbol = group[0].stockSymbol;
    const swapQuote = await deps.swap.getQuote(symbol, inputUnits);
    const swapTxHash = await deps.swap.submit(swapQuote);
    for (let i = 0; i < group.length; i++) {
      group[i] = { ...group[i], status: "swap_pending", stockSwapTxHash: swapTxHash, statusDetail: undefined };
      await putRoundup(group[i]);
    }
    const swapRes = await deps.swap.resolve(swapTxHash, symbol, swapQuote.expectedStockUnits);
    const investedUsdCents = tokenUnitsToUsdCents(swapQuote.inputUnits - swapQuote.feeUnits, 6);
    await this.complete(group, swapRes.stockUnitsReceived, swapRes.txHash, investedUsdCents);
  }

  /** Proportional attribution + celebration event. */
  private async complete(
    group: RoundupInvestment[],
    stockUnits: bigint,
    swapTxHash: Hex,
    investedUsdCents: number,
  ): Promise<void> {
    const deps = this.getDeps();
    const totalPence = group.reduce((s, r) => s + r.roundupPence, 0);
    assertHomogeneousBatch(group);
    const currency = group[0].currency;
    const usdPerCurrencyE6 = await deps.getUsdPerCurrencyE6(currency);
    const investedPenceTotal = Math.min(
      totalPence,
      usdCentsToFiatMinor(investedUsdCents, usdPerCurrencyE6),
    );
    const costPenceTotal = totalPence - investedPenceTotal;

    let stockRemaining = stockUnits;
    let investedRemaining = investedPenceTotal;
    const now = Date.now();
    for (let i = 0; i < group.length; i++) {
      const last = i === group.length - 1;
      const share = last
        ? investedRemaining
        : Math.floor((investedPenceTotal * group[i].roundupPence) / totalPence);
      const stockShare = last
        ? stockRemaining
        : (stockUnits * BigInt(group[i].roundupPence)) / BigInt(totalPence);
      investedRemaining -= share;
      stockRemaining -= stockShare;

      group[i] = {
        ...group[i],
        status: "complete",
        stockSwapTxHash: swapTxHash,
        stockAmountUnits: stockShare.toString(),
        investedPence: share,
        executionCostPence: group[i].roundupPence - share,
        completedAt: now,
        statusDetail: undefined,
      };
      await putRoundup(group[i]);
    }

    deps.onEvent({
      type: "invested",
      stockSymbol: group[0].stockSymbol,
      investedPence: investedPenceTotal,
      roundupPence: totalPence,
      costPence: costPenceTotal,
      count: group.length,
      currency,
    });
  }
}

// ---------- helpers ----------

function errMsg(err: unknown): string {
  return String(err instanceof Error ? err.message : err);
}

function groupBy<T>(items: T[], key: (t: T) => string): Map<string, T[]> {
  const m = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    const arr = m.get(k);
    if (arr) arr.push(item);
    else m.set(k, [item]);
  }
  return m;
}

function assertHomogeneousBatch(group: RoundupInvestment[]): void {
  if (group.length === 0) throw new Error("Cannot execute an empty round-up batch");
  const { stockSymbol, currency } = group[0];
  if (group.some((r) => r.stockSymbol !== stockSymbol || r.currency !== currency)) {
    throw new Error("Round-up batch contains mixed stock targets or currencies");
  }
}

async function annotate(records: RoundupInvestment[], detail: string): Promise<void> {
  for (const r of records) {
    if (r.statusDetail !== detail) await putRoundup({ ...r, statusDetail: detail });
  }
}
